import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralDatepickersFieldsDropdownsMonthRange from 'lumen/components/general/datepickers/fields/dropdowns-month-range';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { DateGranularity } from 'lumen/utils/constants/budget-forecast-constants';
import { localCopy } from 'tracked-toolbox';

export interface HotelBudgetForecastDateRangeSignature {
  Args: {
    endDate: Dayjs;
    granularity: DateGranularity;
    onChange: (options: { endDate: Dayjs; startDate: Dayjs }) => void;
    startDate: Dayjs;
  };
  Element: HTMLDivElement;
}

export default class HotelBudgetForecastDateRange extends Component<HotelBudgetForecastDateRangeSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @localCopy('args.endDate')
  declare endDate: Dayjs;

  @localCopy('args.startDate')
  declare startDate: Dayjs;

  get dayGranularityEndDateMax(): string {
    return this.startDate.add(364, 'days').format('YYYY-MM-DD');
  }

  get monthGranularityMaxDate(): Dayjs {
    return dayjs().add(2, 'year').endOf('year');
  }

  get monthGranularityMinDate(): Dayjs {
    return this.hotelAndMarketService.hotel.pmsFirstCompleteStayDateWithDefault;
  }

  get selectedMonthRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.startDate,
      end: this.endDate,
    };
  }

  @action
  checkAndSetEndDate(): void {
    const endDateMax = dayjs(this.dayGranularityEndDateMax);

    if (this.endDate.isBefore(this.startDate)) {
      this.endDate = endDateMax;
    }

    if (this.endDate.isAfter(endDateMax)) {
      this.endDate = endDateMax;
    }
  }

  @action
  onEndDateChange(date: Dayjs | null): void {
    if (!date || !date.isValid()) {
      return;
    }

    this.endDate = date;
    this.updateOptions();
  }

  @action
  onStartDateChange(date: Dayjs | null): void {
    if (!date || !date.isValid()) {
      return;
    }

    this.startDate = date;
    this.checkAndSetEndDate();
    this.updateOptions();
  }

  @action
  selectMonthRange({ moment }: { moment: { end: Dayjs; start: Dayjs } }): void {
    this.startDate = moment.start;
    this.endDate = moment.end;

    this.updateOptions();
  }

  @action
  updateOptions(): void {
    this.args.onChange({
      startDate: this.startDate,
      endDate: this.endDate,
    });
  }

  <template>
    <div ...attributes>
      {{#if (eq @granularity "day")}}
        <div
          class="grid grid-cols-2 gap-400"
          data-test-id="date-range-selector-day-granularity"
        >
          <div>
            <GeneralFormsFormFieldsDatepickerFormField
              {{! @glint-expect-error: Incorrect type }}
              @defaultCenter={{this.startDate}}
              @dropdownClass="dropdown-target top-[56px]"
              @helpText={{t "settings.budget_forecast.max_12_months"}}
              @isOptional={{true}}
              @label={{t "general.start_date"}}
              @onSelectDate={{this.onStartDateChange}}
              @removePadding={{true}}
              @selectedDate={{this.startDate}}
              @showDaysAround={{false}}
            />
          </div>

          <div>
            <GeneralFormsFormFieldsDatepickerFormField
              {{! @glint-expect-error: Incorrect type }}
              @datePickerMaxDate={{this.dayGranularityEndDateMax}}
              @datePickerMinDate={{this.startDate}}
              @defaultCenter={{this.endDate}}
              @dropdownClass="dropdown-target top-[56px]"
              @isOptional={{true}}
              @label={{t "general.end_date"}}
              @onSelectDate={{this.onEndDateChange}}
              @removePadding={{true}}
              @selectedDate={{this.endDate}}
              @showDaysAround={{false}}
            />
          </div>
        </div>
      {{else}}
        <GeneralDatepickersFieldsDropdownsMonthRange
          @isSecondary={{true}}
          {{! @glint-expect-error: Incorrect type }}
          @maxDate={{this.monthGranularityMaxDate}}
          @maxRange="1 year"
          @minDate={{this.monthGranularityMinDate}}
          @selectRange={{this.selectMonthRange}}
          @selectedRange={{this.selectedMonthRange}}
          @startDateHelpText={{t "settings.budget_forecast.max_12_months"}}
          @unsafe_matchTriggerWidth={{true}}
          data-test-id="date-range-selector-month-granularity"
        />
      {{/if}}
    </div>
  </template>
}
